/**
 * Where stock is on its way to us, and when it is late.
 *
 * A vehicle carries its own status and ETA; a part off it follows the vehicle,
 * and only loose stock carries its own. Being *overdue* is a fact about the
 * calendar, so it is derived rather than stored — a date that has passed cannot
 * be forgotten about just because nobody opened the dashboard that week.
 */

import type { Arrival, ArrivalStatus } from '@/types'
import { daysUntil, etaPhrase, parseEta } from '@/lib/dates'

export const ARRIVAL_LABEL: Record<ArrivalStatus, string> = {
  arriving: 'Arriving soon',
  arrived: 'Arrived',
  available: 'Available',
}

export const ARRIVAL_CHIP: Record<ArrivalStatus, string> = {
  arriving: 'chip-warn',
  arrived: 'chip-neutral',
  available: 'chip-ok',
}

type Timing = Pick<Arrival, 'arrival' | 'eta'> & Partial<Pick<Arrival, 'slippedFrom' | 'delayNotice'>>

/** Once it is here the arrival date stops mattering. */
export function hasArrived(a: Pick<Arrival, 'arrival'>): boolean {
  return a.arrival !== 'arriving'
}

/** Past its estimated arrival (the end of the month, for a month) and still not here. */
export function isOverdue(a: Pick<Arrival, 'arrival' | 'eta'>, now: Date = new Date()): boolean {
  if (hasArrived(a)) return false
  const days = daysUntil(a.eta, now)
  return days !== null && days < 0
}

/** Overdue, or pushed back since customers were first shown a date. */
export function isLate(a: Timing, now: Date = new Date()): boolean {
  return !hasArrived(a) && (isOverdue(a, now) || Boolean(a.slippedFrom))
}

/** How many days late: past the ETA, or else how far the ETA was pushed. 0 if neither. */
export function daysLate(a: Timing, now: Date = new Date()): number {
  if (isOverdue(a, now)) return Math.abs(daysUntil(a.eta, now) ?? 0)
  if (hasArrived(a) || !a.slippedFrom) return 0
  const was = parseEta(a.slippedFrom), is = parseEta(a.eta)
  return was && is ? Math.max(0, Math.round((is.getTime() - was.getTime()) / 86_400_000)) : 0
}

/**
 * Whether the customers still need telling. A notice covers the ETA that was
 * current when it went out, so pushing the date again asks for a fresh one —
 * which is the honest behaviour: the last thing they were told is now wrong.
 */
export function needsDelayNotice(a: Timing, now: Date = new Date()): boolean {
  return isLate(a, now) && a.delayNotice?.eta !== a.eta
}

export function delayPhrase(a: Timing, now: Date = new Date()): string {
  const late = daysLate(a, now)
  const days = late === 1 ? '1 day' : `${late} days`
  if (isOverdue(a, now)) return `${days} past its ETA`
  if (late) return `ETA pushed back ${days}`
  return 'On schedule'
}

/**
 * Whether saving `next` over `before` pushes the ETA later, and if so what to
 * keep as the date it slipped from. The earliest date stands until the item
 * arrives, so a second push still counts from the first promise.
 */
export function slippedFrom(before: Partial<Arrival> | null, next: Pick<Arrival, 'arrival' | 'eta'>): string | null {
  if (hasArrived(next)) return null
  if (before?.slippedFrom) return before.slippedFrom
  if (!before || before.arrival !== 'arriving' || !before.eta || !next.eta) return null
  const was = parseEta(before.eta), is = parseEta(next.eta)
  return was && is && is.getTime() > was.getTime() ? before.eta : null
}

/** One line for a buyer: the status, and the ETA while there is one worth giving. */
export function arrivalLine(a: Pick<Arrival, 'arrival' | 'eta'>, now: Date = new Date()): string {
  if (a.arrival === 'available') return 'Available now'
  if (a.arrival === 'arrived') return 'Arrived, being prepared'
  // A date that has gone by would only advertise the delay; say "soon" instead.
  if (!a.eta || isOverdue(a, now)) return 'Arriving soon'
  return `Arriving ${etaPhrase(a.eta, now)}`
}
